import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    ArrowRight,
    CalendarPlus,
    CheckCircle2,
    Circle,
    CreditCard,
    ExternalLink,
    Image,
    Loader2,
    Mail,
    Rocket,
    Sparkles,
    X
} from 'lucide-react';

const STEP_ICONS = {
    public_page: ExternalLink,
    payment: CreditCard,
    profile: Image,
    photos: Image,
    contact: Mail,
    first_publication: CalendarPlus
};

const GUIDE_STEPS = [
    {
        title: 'Tu checklist manda',
        body: 'Cada punto pendiente tiene una acción directa. Si no sabés por dónde seguir, empezá por el primer item incompleto.'
    },
    {
        title: 'Mercado Pago va primero',
        body: 'Cuando lo conectás, PASSAR puede cobrar entradas o inscripciones y acreditar el dinero en tu cuenta.'
    },
    {
        title: 'Tu página necesita confianza',
        body: 'Logo, biografía y contacto hacen que tus compradores sepan que están en el lugar correcto.'
    },
    {
        title: 'Publicá algo real',
        body: 'El primer evento o torneo activa el circuito principal: link público, venta, QR y control de accesos.'
    }
];

const OnboardingChecklist = ({ slug }) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hidden, setHidden] = useState(false);
    const [showWelcome, setShowWelcome] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [guideIndex, setGuideIndex] = useState(0);
    const [updating, setUpdating] = useState(false);

    const hiddenStorageKey = slug ? `passar:onboarding-checklist-hidden:${slug}` : null;

    useEffect(() => {
        let alive = true;

        const fetchOnboarding = async () => {
            try {
                setLoading(true);
                setHidden(false);
                const { data } = await axios.get(`/api/clients/${slug}/onboarding`);
                if (!alive) return;

                let locallyHidden = false;
                if (hiddenStorageKey && typeof window !== 'undefined') {
                    try {
                        locallyHidden = window.localStorage.getItem(hiddenStorageKey) === 'true';
                    } catch (storageError) {
                        console.warn('No se pudo leer el estado local del onboarding:', storageError);
                    }
                }

                setStatus(data);
                setHidden(Boolean(data.completed && (data.flags?.checklistDismissed || locallyHidden)));
                setShowWelcome(!data.completed && !data.flags?.welcomeDismissed);
            } catch (err) {
                if (!alive) return;
                console.error('Error cargando onboarding:', err);
                setError('No se pudo cargar la guía inicial.');
            } finally {
                if (alive) setLoading(false);
            }
        };

        if (slug) fetchOnboarding();

        return () => {
            alive = false;
        };
    }, [slug, hiddenStorageKey]);

    const nextPendingStep = useMemo(() => {
        if (!status?.steps) return null;
        return status.steps.find(step => !step.completed);
    }, [status]);

    const updateFlags = async (flags) => {
        setUpdating(true);
        try {
            const { data } = await axios.patch(`/api/clients/${slug}/onboarding`, flags);
            setStatus(data);
            return data;
        } catch (err) {
            console.error('Error actualizando onboarding:', err);
            return status;
        } finally {
            setUpdating(false);
        }
    };

    const goToStep = (step) => {
        if (!step?.actionPath) return;

        if (step.id === 'public_page') {
            window.open(status?.publicUrl || step.actionPath, '_blank', 'noopener,noreferrer');
            return;
        }

        navigate(step.actionPath);
    };

    const startGuide = async () => {
        setShowWelcome(false);
        setGuideIndex(0);
        await updateFlags({ welcomeDismissed: true });
        setShowGuide(true);
    };

    const skipWelcome = async () => {
        setShowWelcome(false);
        await updateFlags({ welcomeDismissed: true });
    };

    const finishGuide = async () => {
        setShowGuide(false);
        setGuideIndex(0);
        await updateFlags({ welcomeDismissed: true, tourCompleted: true });
    };

    const hideChecklist = async () => {
        setHidden(true);
        if (hiddenStorageKey && typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(hiddenStorageKey, 'true');
            } catch (storageError) {
                console.warn('No se pudo guardar el estado local del onboarding:', storageError);
            }
        }
        await updateFlags({ checklistDismissed: true });
    };

    if (loading) {
        return (
            <section className="mb-8 p-6 bg-surface border border-white/5 rounded-[32px] flex items-center gap-4">
                <Loader2 size={20} className="text-primary-orange animate-spin" />
                <p className="text-white/40 text-sm font-bold">Preparando guía inicial...</p>
            </section>
        );
    }

    if (error || !status) return null;
    if (hidden || (status.completed && status.flags?.checklistDismissed)) return null;

    return (
        <>
            <section data-tour="onboarding-checklist" className="mb-8 bg-surface border border-white/5 rounded-[32px] overflow-hidden shadow-2xl">
                <div className="p-6 md:p-8 border-b border-white/5 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${status.completed ? 'bg-green-500/10 text-green-500' : 'bg-primary-orange/10 text-primary-orange'}`}>
                            {status.completed ? <CheckCircle2 size={28} /> : <Rocket size={28} />}
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[3px] text-white/30 mb-2">Primeros pasos</p>
                            <h2 className="text-2xl font-black text-white leading-tight">
                                {status.completed ? 'Tu espacio ya está listo para vender' : `Configurá tu ${status.organizationLabel.toLowerCase()}`}
                            </h2>
                            <p className="text-white/40 text-sm font-medium mt-2 max-w-2xl">
                                {status.completed
                                    ? 'Completaste la configuración inicial. Podés seguir creando, vendiendo y compartiendo.'
                                    : 'Seguí esta guía corta para dejar todo preparado antes de invitar compradores.'}
                            </p>
                        </div>
                    </div>

                    <div className="min-w-[220px]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-[2px] text-white/30">Progreso</span>
                            <span className="text-sm font-black text-white">{status.completedCount}/{status.totalSteps}</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-700 ${status.completed ? 'bg-green-500' : 'bg-brand-gradient'}`}
                                style={{ width: `${status.progress}%` }}
                            />
                        </div>
                    </div>
                </div>

                <div className="p-4 md:p-6 grid gap-3">
                    {status.steps.map((step) => {
                        const StepIcon = STEP_ICONS[step.id] || Circle;
                        return (
                            <div key={step.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/5">
                                <div className="flex items-start gap-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${step.completed ? 'bg-green-500/10 text-green-500' : 'bg-white/5 text-white/30'}`}>
                                        {step.completed ? <CheckCircle2 size={20} /> : <StepIcon size={20} />}
                                    </div>
                                    <div>
                                        <h3 className={`font-black text-sm ${step.completed ? 'text-white/60' : 'text-white'}`}>{step.label}</h3>
                                        <p className="text-xs text-white/35 font-medium mt-1 leading-relaxed">{step.description}</p>
                                    </div>
                                </div>

                                {step.completed ? (
                                    <span className="self-start sm:self-center px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-widest">
                                        Listo
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => goToStep(step)}
                                        className="self-start sm:self-center px-4 py-2 rounded-xl bg-white/5 hover:bg-primary-orange hover:text-white text-white/60 border border-white/10 hover:border-primary-orange transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                                    >
                                        Resolver
                                        <ArrowRight size={14} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="px-6 pb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            setGuideIndex(0);
                            setShowGuide(true);
                        }}
                        className="text-xs font-black uppercase tracking-widest text-white/40 hover:text-primary-orange transition-colors flex items-center gap-2"
                    >
                        <Sparkles size={14} />
                        Ver guía rápida
                    </button>

                    {status.completed ? (
                        <button
                            type="button"
                            onClick={hideChecklist}
                            disabled={updating}
                            className="text-xs font-black uppercase tracking-widest text-white/30 hover:text-white transition-colors"
                        >
                            Ocultar checklist
                        </button>
                    ) : nextPendingStep ? (
                        <button
                            type="button"
                            onClick={() => goToStep(nextPendingStep)}
                            className="px-5 py-3 rounded-2xl bg-brand-gradient text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                        >
                            Continuar configuración
                            <ArrowRight size={16} />
                        </button>
                    ) : null}
                </div>
            </section>

            {showWelcome && (
                <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-surface border border-white/10 rounded-[32px] shadow-2xl overflow-hidden">
                        <div className="h-1 bg-brand-gradient" />
                        <div className="p-8">
                            <div className="w-14 h-14 rounded-2xl bg-primary-orange/10 text-primary-orange flex items-center justify-center mb-6">
                                <Rocket size={28} />
                            </div>
                            <p className="text-[10px] font-black uppercase tracking-[3px] text-white/30 mb-3">Bienvenida a PASSAR</p>
                            <h2 className="text-3xl font-black text-white tracking-tight leading-tight mb-4">
                                Dejá tu {status.organizationLabel.toLowerCase()} listo para vender
                            </h2>
                            <p className="text-white/45 text-sm font-medium leading-relaxed mb-8">
                                Te vamos a guiar por los pasos clave: conectar Mercado Pago, completar tu marca, cargar contacto y publicar tu primera venta.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={startGuide}
                                    disabled={updating}
                                    className="flex-1 px-5 py-4 rounded-2xl bg-brand-gradient text-white font-black uppercase tracking-widest text-xs"
                                >
                                    Empezar configuración
                                </button>
                                <button
                                    type="button"
                                    onClick={skipWelcome}
                                    disabled={updating}
                                    className="px-5 py-4 rounded-2xl bg-white/5 text-white/50 hover:text-white border border-white/10 font-black uppercase tracking-widest text-xs"
                                >
                                    Ver después
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showGuide && (
                <div className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-[#101010] border border-white/10 rounded-[32px] shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-white/5 flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-[3px] text-primary-orange">
                                Guía {guideIndex + 1}/{GUIDE_STEPS.length}
                            </span>
                            <button
                                type="button"
                                onClick={finishGuide}
                                className="w-9 h-9 rounded-full bg-white/5 text-white/40 hover:text-white flex items-center justify-center"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-8">
                            <h3 className="text-2xl font-black text-white mb-3">{GUIDE_STEPS[guideIndex].title}</h3>
                            <p className="text-white/45 text-sm font-medium leading-relaxed mb-8">{GUIDE_STEPS[guideIndex].body}</p>
                            <div className="flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => setGuideIndex(Math.max(guideIndex - 1, 0))}
                                    disabled={guideIndex === 0}
                                    className="px-4 py-3 rounded-xl bg-white/5 text-white/40 disabled:opacity-30 font-black uppercase tracking-widest text-[10px]"
                                >
                                    Atrás
                                </button>
                                {guideIndex === GUIDE_STEPS.length - 1 ? (
                                    <button
                                        type="button"
                                        onClick={finishGuide}
                                        className="px-5 py-3 rounded-xl bg-brand-gradient text-white font-black uppercase tracking-widest text-[10px]"
                                    >
                                        Entendido
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setGuideIndex(Math.min(guideIndex + 1, GUIDE_STEPS.length - 1))}
                                        className="px-5 py-3 rounded-xl bg-brand-gradient text-white font-black uppercase tracking-widest text-[10px]"
                                    >
                                        Siguiente
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OnboardingChecklist;
